// src/pages/shared/SessionCalendar.jsx
// Session calendar for ADMIN, MANAGER, COACH, and STUDENT roles.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../../api";
import { listSessionsCalendar, listCalendarUsers } from "../../api/sessions";
import { getNormalizedRole } from "../../utils/auth";
import { useParentChild } from "../../context/ParentChildContext";
import ParentPortalHeader from "../../components/parent/ParentPortalHeader";
import SessionJoinAction from "../../components/classroom/SessionJoinAction";
import SessionHostAction from "../../components/classroom/SessionHostAction";
import { formatTime, formatDate, formatWeekRange, academyTodayDate, todayIso } from "../../utils/regionalFormat";
import { localizeBatchName } from '../../utils/displayNames';
import ListPageLayout, {
    ListPageHeader,
    ListLoadingSpinner,
    ListSectionCard,
    ListTableCard,
} from "../../components/layout/ListPageLayout";

function expandHolidaysToMap(holidays, holidayFallback) {
    const map = {};
    (holidays || []).forEach((h) => {
        if (!h?.startDate) return;
        const start = new Date(h.startDate + "T00:00:00");
        const end = new Date((h.endDate || h.startDate) + "T00:00:00");
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            map[toIso(d)] = h.name || holidayFallback;
        }
    });
    return map;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_VISIBLE_MONTH = 3;
const WEEK_HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 06:00–22:00

const BATCH_PALETTE = [
    { bg: "bg-violet-100", text: "text-violet-800", dot: "bg-violet-500", ring: "ring-violet-300" },
    { bg: "bg-sky-100", text: "text-sky-800", dot: "bg-sky-500", ring: "ring-sky-300" },
    { bg: "bg-rose-100", text: "text-rose-800", dot: "bg-rose-500", ring: "ring-rose-300" },
    { bg: "bg-teal-100", text: "text-teal-800", dot: "bg-teal-500", ring: "ring-teal-300" },
    { bg: "bg-orange-100", text: "text-orange-800", dot: "bg-orange-500", ring: "ring-orange-300" },
    { bg: "bg-cyan-100", text: "text-cyan-800", dot: "bg-cyan-500", ring: "ring-cyan-300" },
    { bg: "bg-fuchsia-100", text: "text-fuchsia-800", dot: "bg-fuchsia-500", ring: "ring-fuchsia-300" },
    { bg: "bg-lime-100", text: "text-lime-800", dot: "bg-lime-500", ring: "ring-lime-300" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const toIso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const toTime = (iso) => (iso ? formatTime(iso) : "");

const startOfCalendarGrid = (year, month) => {
    const first = new Date(year, month, 1);
    const dow = first.getDay();
    const offset = dow === 0 ? 6 : dow - 1;
    return new Date(year, month, 1 - offset);
};

const startOfWeek = (date) => {
    const d = new Date(date);
    const dow = d.getDay();
    const offset = dow === 0 ? 6 : dow - 1;
    d.setDate(d.getDate() - offset);
    d.setHours(0, 0, 0, 0);
    return d;
};

const hashBatch = (id) => {
    const n = Number(id) || 0;
    return Math.abs((n * 2654435761) % 2147483647);
};

const batchColor = (batchId) => BATCH_PALETTE[hashBatch(batchId) % BATCH_PALETTE.length];

const monthName = (t, monthIndex) => t(`calendar.months.${monthIndex + 1}`);

const dayShortFromIndex = (t, index) => t(`days.short.${index + 1}`);

const dayShortFromDate = (t, date) => t(`days.short.${date.getDay() === 0 ? 7 : date.getDay()}`);

const statusStyle = (status) => {
    switch ((status || "").toUpperCase()) {
        case "COMPLETED": return { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500" };
        default: return { bg: "bg-amber-100", text: "text-amber-800", dot: "bg-amber-500" };
    }
};

const chipStyle = (session, useBatchColor) => {
    if (useBatchColor && session.batchId != null) {
        const c = batchColor(session.batchId);
        return { bg: c.bg, text: c.text, dot: c.dot };
    }
    return statusStyle(session.status);
};

const attendanceLabel = (session, t) => {
    const raw = (session.attendanceStatus || session.attendanceType || "").toUpperCase();
    if (raw) {
        if (raw === "PRESENT") return { text: t("calendar.present"), cls: "text-emerald-700", icon: "✓" };
        if (raw === "ABSENT") return { text: t("calendar.absent"), cls: "text-red-600", icon: "✗" };
        return { text: raw, cls: "text-gray-600", icon: "•" };
    }
    if ((session.status || "").toUpperCase() !== "COMPLETED") return null;
    if (session.attendanceMarked === true) {
        return { text: t("calendar.marked"), cls: "text-emerald-700", icon: "✓" };
    }
    return { text: t("calendar.notMarked"), cls: "text-amber-600" };
};

const sessionHour = (iso) => {
    if (!iso) return 6;
    try { return new Date(iso).getHours(); } catch { return 6; }
};

const formatBatchDisplay = (name, t) => {
    if (!name) return t("calendar.session");
    const localized = localizeBatchName(name, t);
    return String(localized || name).replace(/_/g, " ").replace(/\s+/g, " ").trim();
};

function TooltipRow({ label, children }) {
    return (
        <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 items-start py-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 pt-0.5 leading-snug">
                {label}
            </span>
            <div className="min-w-0 text-sm text-gray-800 leading-snug">{children}</div>
        </div>
    );
}

// ─── Month / year picker ─────────────────────────────────────────────────────

function MonthYearPicker({ year, month, onSelect, onClose }) {
    const { t } = useTranslation();
    const [pickYear, setPickYear] = useState(year);
    const ref = useRef(null);

    useEffect(() => {
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [onClose]);

    return (
        <div ref={ref} className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-72">
            <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={() => setPickYear(y => y - 1)} className="p-1 rounded hover:bg-gray-100 text-gray-500" aria-label={t("calendar.prevYear")}>‹</button>
                <span className="font-semibold text-gray-900">{pickYear}</span>
                <button type="button" onClick={() => setPickYear(y => y + 1)} className="p-1 rounded hover:bg-gray-100 text-gray-500" aria-label={t("calendar.nextYear")}>›</button>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
                {Array.from({ length: 12 }, (_, idx) => {
                    const name = monthName(t, idx);
                    return (
                    <button
                        key={idx}
                        type="button"
                        onClick={() => { onSelect(pickYear, idx); onClose(); }}
                        className={`px-2 py-2 text-xs rounded-lg font-medium transition-colors ${pickYear === year && idx === month ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white" : "text-gray-700 hover:bg-purple-50"}`}
                    >
                        {name.slice(0, 3)}
                    </button>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Day detail modal ─────────────────────────────────────────────────────────

function DayDetailModal({ date, sessions, onClose, childrenProps }) {
    const { t, i18n } = useTranslation();
    const label = formatDate(date);
    const sorted = [...sessions].sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col"
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-labelledby="day-detail-title"
            >
                <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-gray-100">
                    <div>
                        <h3 id="day-detail-title" className="text-lg font-bold text-gray-900">{label}</h3>
                        <p className="text-sm text-gray-500">{t("calendar.sessionsCount", { count: sorted.length })}</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1" aria-label={t("calendar.close")}>✕</button>
                </div>
                <div className="overflow-y-auto px-5 py-4 flex flex-col gap-2">
                    {sorted.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-6">{t("calendar.noSessionsDay")}</p>
                    ) : sorted.map(s => (
                        <div key={s.sessionId} className="rounded-lg border border-gray-100 p-2">
                            {childrenProps.renderSessionRow(s)}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
}

// ─── Session chip ─────────────────────────────────────────────────────────────

function SessionTooltip({ session, anchorRef, visible, useBatchColor }) {
    const { t } = useTranslation();
    const tipRef = useRef(null);
    const [pos, setPos] = useState(null);
    const batchTitle = formatBatchDisplay(session.batchName, t);
    const chip = useBatchColor && session.batchId != null
        ? batchColor(session.batchId)
        : statusStyle(session.status);

    const measurePosition = useCallback(() => {
        if (!anchorRef.current || !tipRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const tip = tipRef.current;
        const tipW = tip.offsetWidth;
        const tipH = tip.offsetHeight;
        let top = rect.bottom + 8;
        if (top + tipH > window.innerHeight - 8) {
            top = Math.max(8, rect.top - tipH - 8);
        }
        let left = rect.left + rect.width / 2 - tipW / 2;
        left = Math.min(Math.max(8, left), window.innerWidth - tipW - 8);
        setPos({ top, left });
    }, [anchorRef]);

    useLayoutEffect(() => {
        if (!visible || !anchorRef.current || !tipRef.current) {
            setPos(null);
            return;
        }
        measurePosition();
    }, [visible, session, anchorRef, batchTitle, measurePosition]);

    useEffect(() => {
        if (!visible) return;
        window.addEventListener("resize", measurePosition);
        return () => window.removeEventListener("resize", measurePosition);
    }, [visible, measurePosition]);

    if (!visible) return null;
    const att = attendanceLabel(session, t);
    const hidden = pos === null;
    const status = statusStyle(session.status);

    return createPortal(
        <div
            ref={tipRef}
            style={{
                position: "fixed",
                top: hidden ? -9999 : pos.top,
                left: hidden ? -9999 : pos.left,
                zIndex: 9999,
                visibility: hidden ? "hidden" : "visible",
            }}
            className="pointer-events-none w-max max-w-[min(calc(100vw-1rem),42rem)] rounded-xl border border-gray-200/90 bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden"
            role="tooltip"
        >
            <div className={`px-3.5 py-2.5 border-b border-gray-100 ${chip.bg}`}>
                <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${chip.dot}`} />
                    <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{batchTitle}</p>
                </div>
            </div>
            <div className="px-3.5 py-3 space-y-1 bg-white">
                <TooltipRow label={t("calendar.level")}>{session.levelName || "—"}</TooltipRow>
                <TooltipRow label={t("calendar.sessionCoach")}>{session.coachFullName || session.coachName || "—"}</TooltipRow>
                <TooltipRow label={t("calendar.time")}>
                    <span className="font-semibold tabular-nums">
                        {toTime(session.startTime)} – {toTime(session.endTime)}
                    </span>
                </TooltipRow>
                <TooltipRow label={t("calendar.sessionStatus")}>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${status.bg} ${status.text}`}>
                        {(() => {
                            const raw = String(session.status || "").toUpperCase();
                            if (raw === "PENDING") return t("calendar.legendPending");
                            if (raw === "COMPLETED") return t("calendar.legendCompleted");
                            if (raw === "CANCELLED") return t("calendar.legendCancelled");
                            return session.status || "—";
                        })()}
                    </span>
                </TooltipRow>
                {att && (
                    <TooltipRow label={t("calendar.attendance")}>
                        <span className={`font-semibold ${att.cls}`}>
                            {att.icon && <span className="mr-1">{att.icon}</span>}
                            {att.text}
                        </span>
                    </TooltipRow>
                )}
                {session.sessionNotes && (
                    <div className="pt-2 mt-1 border-t border-gray-100">
                        <TooltipRow label={t("calendar.sessionNotes")}>
                            <span className="text-gray-600">{session.sessionNotes}</span>
                        </TooltipRow>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

function SessionChip({ session, baseUrl, navigate, readOnly, useBatchColor, compact }) {
    const { t } = useTranslation();
    const s = chipStyle(session, useBatchColor);
    const att = attendanceLabel(session, t);
    const [showTip, setShowTip] = useState(false);
    const btnRef = useRef(null);

    const handleMouseEnter = () => setShowTip(true);
    const handleMouseLeave = () => setShowTip(false);

    const handleClick = (e) => {
        e.stopPropagation();
        if (readOnly) return;
        navigate(`${baseUrl}/sessions/${session.sessionId}/attendance`, {
            state: {
                sessionId: session.sessionId,
                batchId: session.batchId,
                batchName: session.batchName,
                levelName: session.levelName,
                coachFullName: session.coachName || session.coachFullName,
                coachId: session.coachId,
                status: session.status,
                startTime: session.startTime,
                endTime: session.endTime,
                returnTo: `${baseUrl}/calendar`,
            },
        });
    };

    useEffect(() => {
        if (!showTip) return;
        const hide = () => setShowTip(false);
        window.addEventListener("scroll", hide, true);
        return () => window.removeEventListener("scroll", hide, true);
    }, [showTip]);

    return (
        <div>
            <button
                ref={btnRef}
                type="button"
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={`w-full text-left rounded px-1.5 py-0.5 font-medium leading-tight truncate cursor-pointer hover:opacity-80 transition-opacity ${compact ? "text-[10px]" : "text-[11px]"} ${s.bg} ${s.text}`}
            >
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 flex-shrink-0 align-middle ${s.dot}`} />
                {toTime(session.startTime)} {localizeBatchName(session.batchName, t) || `#${session.sessionId}`}
                {att && (
                    <span className={`ml-1 font-semibold ${att.cls}`}>
                        {att.icon && <span className="mr-0.5">{att.icon}</span>}{att.text}
                    </span>
                )}
            </button>
            <SessionTooltip session={session} anchorRef={btnRef} visible={showTip} useBatchColor={useBatchColor} />
        </div>
    );
}

// ─── Day cell (month view) ───────────────────────────────────────────────────

function DayCell({
    date, isCurrentMonth, isToday, sessions, baseUrl, navigate, readOnly,
    useBatchColor, onDayClick, onShowAll, holidayLabel,
}) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const sorted = [...sessions].sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
    const maxVisible = expanded ? sorted.length : MAX_VISIBLE_MONTH;
    const visible = sorted.slice(0, maxVisible);
    const overflow = sorted.length - MAX_VISIBLE_MONTH;

    const handleCellClick = (e) => {
        if (e.target.closest("button")) return;
        if (readOnly) {
            if (sorted.length > 0) onShowAll(date, sorted);
            return;
        }
        onDayClick(date);
    };

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleCellClick}
            onKeyDown={(e) => { if (e.key === "Enter") handleCellClick(e); }}
            className={`min-h-[90px] p-1.5 border-b border-r border-gray-100 flex flex-col gap-0.5 cursor-pointer hover:bg-purple-50/30 transition-colors ${holidayLabel ? "bg-rose-50/70" : isCurrentMonth ? "bg-white" : "bg-gray-50/60"} ${isToday ? "ring-2 ring-inset ring-purple-400" : ""}`}
        >
            <div className="flex items-center justify-between w-full flex-shrink-0 gap-1 mb-0.5">
                <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${isToday ? "bg-gradient-to-br from-purple-600 to-indigo-600 text-white" : isCurrentMonth ? "text-gray-700" : "text-gray-300"}`}>
                    {date.getDate()}
                </span>
                {holidayLabel && (
                    <span className="text-[9px] font-semibold text-rose-700 truncate max-w-[72px]" title={holidayLabel}>
                        🏖 {holidayLabel}
                    </span>
                )}
                {sorted.length > 0 && !expanded && (
                    <span
                        className="min-w-[18px] h-[18px] px-1 rounded-md bg-purple-100 text-purple-700 text-[10px] font-bold flex items-center justify-center leading-none flex-shrink-0"
                        title={t("calendar.sessionsCount", { count: sorted.length })}
                    >
                        {sorted.length > 9 ? "9+" : sorted.length}
                    </span>
                )}
            </div>
            <div className="flex flex-col gap-0.5 w-full overflow-hidden flex-1">
                {visible.map((s) => (
                    <SessionChip key={s.sessionId} session={s} baseUrl={baseUrl} navigate={navigate} readOnly={readOnly} useBatchColor={useBatchColor} />
                ))}
                {overflow > 0 && !expanded && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onShowAll(date, sorted); }}
                        className="text-[10px] text-purple-600 hover:text-purple-800 font-medium text-left pl-1 hover:underline"
                    >
                        {t("calendar.more", { count: overflow })}
                    </button>
                )}
                {expanded && overflow > 0 && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
                        className="text-[10px] text-gray-400 pl-1 hover:text-gray-600"
                    >
                        {t("calendar.showLess")}
                    </button>
                )}
            </div>
        </div>
    );
}

// ─── Week view ─────────────────────────────────────────────────────────────────

function WeekView({ weekStart, sessions, baseUrl, navigate, readOnly, useBatchColor, onDayClick, onShowAll }) {
    const { t, i18n } = useTranslation();
    const days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return d;
    });

    const byDate = {};
    sessions.forEach((s) => {
        if (!s.startTime) return;
        const key = s.startTime.slice(0, 10);
        if (!byDate[key]) byDate[key] = [];
        byDate[key].push(s);
    });

    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm flex flex-col min-h-[480px]">
            <div className="grid grid-cols-8 bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <div className="py-2 px-2 text-xs font-semibold text-gray-400 border-r border-gray-200" />
                {days.map((d) => {
                    const key = toIso(d);
                    const isToday = key === todayIso();
                    const daySessions = byDate[key] || [];
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => daySessions.length > 0 && onShowAll(d, daySessions)}
                            className={`py-2 text-center border-r last:border-r-0 border-gray-200 hover:bg-purple-50 transition-colors ${isToday ? "bg-purple-50" : ""}`}
                        >
                            <div className="text-[10px] text-gray-500 uppercase">{dayShortFromDate(t, d)}</div>
                            <div className={`text-sm font-bold ${isToday ? "text-purple-600" : "text-gray-800"}`}>{d.getDate()}</div>
                            {daySessions.length > 0 && (
                                <div className="text-[10px] text-purple-600 font-medium">{t("calendar.sessAbbr", { count: daySessions.length })}</div>
                            )}
                        </button>
                    );
                })}
            </div>
            <div className="flex-1 overflow-y-auto">
                {WEEK_HOURS.map((hour) => (
                    <div key={hour} className="grid grid-cols-8 border-b border-gray-100 min-h-[44px]">
                        <div className="px-2 py-1 text-[10px] text-gray-400 border-r border-gray-100 font-mono">
                            {String(hour).padStart(2, "0")}:00
                        </div>
                        {days.map((d) => {
                            const key = toIso(d);
                            const hourSessions = (byDate[key] || []).filter(s => sessionHour(s.startTime) === hour);
                            return (
                                <div
                                    key={`${key}-${hour}`}
                                    className="border-r last:border-r-0 border-gray-100 p-0.5 relative"
                                    onClick={() => !readOnly && hourSessions.length === 0 && onDayClick(d)}
                                >
                                    {hourSessions.map(s => (
                                        <SessionChip
                                            key={s.sessionId}
                                            session={s}
                                            baseUrl={baseUrl}
                                            navigate={navigate}
                                            readOnly={readOnly}
                                            useBatchColor={useBatchColor}
                                            compact
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Batch filter pills ──────────────────────────────────────────────────────

function BatchFilterBar({ batches, selectedBatchIds, onSelectBatch, onShowAll }) {
    const { t } = useTranslation();
    if (batches.length === 0) return null;
    const showAll = selectedBatchIds.size === 0;
    return (
        <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-500 mr-1">{t("calendar.batches")}</span>
                <button
                    type="button"
                    onClick={onShowAll}
                    className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${showAll ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-purple-600" : "border-gray-300 text-gray-600 hover:bg-purple-50"}`}
                >
                    {t("calendar.showAll")}
                </button>
                {batches.map(({ id, name }) => {
                    const selected = selectedBatchIds.has(id);
                    const c = batchColor(id);
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => onSelectBatch(id)}
                            aria-pressed={!showAll && selected}
                            className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-all flex items-center gap-1.5 ${showAll
                                ? "border-gray-200 text-gray-600 bg-white hover:border-gray-300"
                                : selected
                                    ? `${c.bg} ${c.text} border-transparent ring-2 ring-offset-1 ${c.ring}`
                                    : "opacity-45 border-gray-200 text-gray-400 bg-gray-50 hover:opacity-70"
                                }`}
                        >
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${selected || showAll ? c.dot : "bg-gray-300"}`} />
                            {name}
                        </button>
                    );
                })}
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">
                {showAll
                    ? t("calendar.batchFilterHintAll")
                    : t("calendar.batchFilterHintSelected", { count: selectedBatchIds.size })}
            </p>
        </div>
    );
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend({ readOnly, useBatchColor }) {
    const { t } = useTranslation();
    return (
        <div className="flex items-center gap-3 flex-wrap">
            {!useBatchColor && [
                { label: t("calendar.legendPending"), dot: "bg-amber-500" },
                { label: t("calendar.legendCompleted"), dot: "bg-emerald-500" },
                { label: t("calendar.legendCancelled"), dot: "bg-red-400" },
            ].map(({ label, dot }) => (
                <span key={label} className="flex items-center gap-1 text-xs text-gray-500">
                    <span className={`w-2 h-2 rounded-full ${dot}`} />
                    {label}
                </span>
            ))}
            {useBatchColor && (
                <span className="text-xs text-gray-500 italic">{t("calendar.legendBatchColors")}</span>
            )}
            <span className="text-xs text-rose-600">{t("calendar.legendHoliday")}</span>
            <span className="text-xs text-purple-500 italic">{t("calendar.legendHover")}</span>
            {readOnly ? (
                <>
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                        <span className="text-emerald-600 font-semibold">{t("calendar.legendPresent")}</span>
                        <span className="text-red-600 font-semibold">{t("calendar.legendAbsent")}</span>
                    </span>
                    <span className="text-xs text-gray-500 italic">{t("calendar.legendClickDay")}</span>
                </>
            ) : (
                <span className="text-xs text-gray-500 italic">{t("calendar.legendClickAttendance")}</span>
            )}
        </div>
    );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function SessionCalendar({ roleProp }) {
    const { t, i18n } = useTranslation();
    const role = getNormalizedRole(roleProp);
    const navigate = useNavigate();
    const { childStudentId } = useParentChild();
    const readOnly = role === "STUDENT" || role === "PARENT";
    const canUseViewingPicker = !readOnly && (role === "ADMIN" || role === "MANAGER" || role === "COACH");
    const isCoachCalendar = role === "COACH";
    const baseUrl = role === "ADMIN" ? "/admin"
        : role === "MANAGER" ? "/manager"
        : role === "STUDENT" ? "/student"
        : role === "PARENT" ? "/parent"
        : "/coach";
    const useBatchColor = !readOnly;

    const today = academyTodayDate();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth());
    const [viewMode, setViewMode] = useState("month"); // month | week
    const [weekAnchor, setWeekAnchor] = useState(startOfWeek(today));
    const [showPicker, setShowPicker] = useState(false);
    const [selectedBatchIds, setSelectedBatchIds] = useState(() => new Set());
    const [dayModal, setDayModal] = useState(null); // { date, sessions }

    // ─── "View others" state ────────────────────────────────────────────────
    const [viewingUser, setViewingUser] = useState(null);   // null = self, { id, label, type } = someone else
    const [userList, setUserList] = useState([]);            // populated once for non-student roles
    const [userListLoadError, setUserListLoadError] = useState(false);
    const [userSearch, setUserSearch] = useState("");        // filter text in the picker dropdown
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerButtonRef = useRef(null);
    const pickerMenuRef = useRef(null);
    const [pickerMenuPos, setPickerMenuPos] = useState({ top: 0, left: 0 });

    const [sessions, setSessions] = useState([]);
    const [holidayByDate, setHolidayByDate] = useState({});
    const [holidayLoadError, setHolidayLoadError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const containerRef = useRef(null);

    // Load user list once for the "Viewing:" picker
    useEffect(() => {
        if (readOnly) return;
        listCalendarUsers(role)
            .then((rows) => {
                setUserList(rows || []);
                setUserListLoadError(false);
            })
            .catch(() => {
                setUserList([]);
                setUserListLoadError(true);
            });
    }, [role, readOnly]);

    // Position + close viewing picker (portal menu avoids hero card overflow clipping)
    useLayoutEffect(() => {
        if (!pickerOpen || !pickerButtonRef.current) return;
        const rect = pickerButtonRef.current.getBoundingClientRect();
        const menuWidth = 288;
        let left = rect.left;
        if (left + menuWidth > window.innerWidth - 12) {
            left = Math.max(12, window.innerWidth - menuWidth - 12);
        }
        setPickerMenuPos({ top: rect.bottom + 8, left });
    }, [pickerOpen]);

    useEffect(() => {
        if (!pickerOpen) return;
        const close = (e) => {
            if (pickerButtonRef.current?.contains(e.target)) return;
            if (pickerMenuRef.current?.contains(e.target)) return;
            setPickerOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setPickerOpen(false); };
        document.addEventListener("mousedown", close);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", close);
            document.removeEventListener("keydown", onKey);
        };
    }, [pickerOpen]);

    const gridStart = startOfCalendarGrid(year, month);
    const gridDays = Array.from({ length: 42 }, (_, i) => {
        const d = new Date(gridStart);
        d.setDate(gridStart.getDate() + i);
        return d;
    });

    const weekStart = viewMode === "week" ? weekAnchor : startOfWeek(new Date(year, month, 1));
    const weekDays = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return d;
    });

    const fetchFrom = viewMode === "week" ? toIso(weekDays[0]) : toIso(gridDays[0]);
    const fetchTo = viewMode === "week" ? toIso(weekDays[6]) : toIso(gridDays[gridDays.length - 1]);

    const load = useCallback(async () => {
        if (role === "PARENT" && !childStudentId) {
            setSessions([]);
            setLoading(false);
            setError("");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const res = await listSessionsCalendar(role, fetchFrom, fetchTo, viewingUser?.id ?? null, childStudentId);
            setSessions(Array.isArray(res.data) ? res.data : []);
        } catch {
            setError(t("calendar.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [role, fetchFrom, fetchTo, viewingUser, childStudentId, t]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await API.get("/calendar/holidays", { params: { from: fetchFrom, to: fetchTo } });
                if (!cancelled) {
                    setHolidayByDate(expandHolidaysToMap(res.data, t("calendar.holiday")));
                    setHolidayLoadError(false);
                }
            } catch {
                if (!cancelled) {
                    setHolidayByDate({});
                    setHolidayLoadError(true);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [fetchFrom, fetchTo, t]);

    const visibleSessions = useMemo(() => {
        if (selectedBatchIds.size === 0) return sessions;
        return sessions.filter(s => selectedBatchIds.has(s.batchId));
    }, [sessions, selectedBatchIds]);

    const byDate = useMemo(() => {
        const map = {};
        visibleSessions.forEach((s) => {
            if (!s.startTime) return;
            const key = s.startTime.slice(0, 10);
            if (!map[key]) map[key] = [];
            map[key].push(s);
        });
        return map;
    }, [visibleSessions]);

    const batchList = useMemo(() => {
        const seen = new Map();
        sessions.forEach(s => {
            if (s.batchId != null && !seen.has(s.batchId)) {
                seen.set(s.batchId, localizeBatchName(s.batchName, t) || t("batches.batchNumber", { id: s.batchId }));
            }
        });
        return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [sessions, t]);

    const prev = () => {
        if (viewMode === "week") {
            const d = new Date(weekAnchor);
            d.setDate(d.getDate() - 7);
            setWeekAnchor(d);
            setYear(d.getFullYear());
            setMonth(d.getMonth());
        } else if (month === 0) { setYear(y => y - 1); setMonth(11); }
        else setMonth(m => m - 1);
    };

    const next = () => {
        if (viewMode === "week") {
            const d = new Date(weekAnchor);
            d.setDate(d.getDate() + 7);
            setWeekAnchor(d);
            setYear(d.getFullYear());
            setMonth(d.getMonth());
        } else if (month === 11) { setYear(y => y + 1); setMonth(0); }
        else setMonth(m => m + 1);
    };

    const goToday = () => {
        setYear(today.getFullYear());
        setMonth(today.getMonth());
        setWeekAnchor(startOfWeek(today));
    };

    const handleDayClick = (date) => {
        if (readOnly) return;
        navigate(`${baseUrl}/sessions`, { state: { calendarDate: toIso(date) } });
    };

    const selectBatch = (id) => {
        setSelectedBatchIds(prev => {
            if (prev.size === 0) return new Set([id]);
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const showAllBatches = () => setSelectedBatchIds(new Set());

    const prevRef = useRef(prev);
    const nextRef = useRef(next);
    const todayRef = useRef(goToday);
    prevRef.current = prev;
    nextRef.current = next;
    todayRef.current = goToday;

    useEffect(() => {
        const onKey = (e) => {
            if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
            if (e.key === "ArrowLeft") { e.preventDefault(); prevRef.current(); }
            if (e.key === "ArrowRight") { e.preventDefault(); nextRef.current(); }
            if (e.key === "t" || e.key === "T") { e.preventDefault(); todayRef.current(); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const todayKey = toIso(today);
    const monthStart = toIso(new Date(year, month, 1));
    const monthEnd = toIso(new Date(year, month + 1, 0));
    const monthSessions = visibleSessions.filter(s => {
        const d = s.startTime ? s.startTime.slice(0, 10) : "";
        return d >= monthStart && d <= monthEnd;
    });
    const counts = monthSessions.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});
    const monthHasNoSessions = !loading && !error && monthSessions.length === 0;

    const headerLabel = viewMode === "week"
        ? formatWeekRange(weekDays[0], weekDays[6])
        : `${monthName(t, month)} ${year}`;

    const chipRowProps = { baseUrl, navigate, readOnly, useBatchColor };

    const defaultViewLabel = role === "ADMIN" ? t("calendar.allSessions")
        : role === "MANAGER" ? t("calendar.myBranches")
        : t("calendar.mySessions");

    if (loading) {
        return (
            <ListPageLayout>
                <ListPageHeader
                    eyebrow={t("calendar.eyebrow")}
                    title={t("calendar.title")}
                    subtitle={readOnly ? t("calendar.loadingSubtitle") : t("calendar.subtitleStaff")}
                />
                <ListLoadingSpinner />
            </ListPageLayout>
        );
    }

    const viewingPickerMenu = pickerOpen && canUseViewingPicker && createPortal(
        <div
            ref={pickerMenuRef}
            className="calendar-viewing-picker-menu"
            style={{ position: "fixed", top: pickerMenuPos.top, left: pickerMenuPos.left, zIndex: 9999 }}
            role="listbox"
        >
            <div className="border-b border-gray-100 p-2">
                <input
                    type="text"
                    placeholder={t("calendar.searchName")}
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary-300"
                    autoFocus
                />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
                <button
                    type="button"
                    onClick={() => { setViewingUser(null); setPickerOpen(false); setSelectedBatchIds(new Set()); }}
                    className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-primary-50 ${viewingUser === null ? "bg-primary-50 font-semibold text-primary-700" : "text-gray-700"}`}
                >
                    <span className="h-2 w-2 rounded-full bg-primary-400" />
                    {defaultViewLabel}
                </button>
                {userList.filter(u => u.type === "COACH" && (!userSearch.trim() || u.label.toLowerCase().includes(userSearch.toLowerCase()))).length > 0 && !isCoachCalendar && (
                    <>
                        <div className="px-4 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{t("calendar.coaches")}</div>
                        {userList
                            .filter(u => u.type === "COACH" && (!userSearch.trim() || u.label.toLowerCase().includes(userSearch.toLowerCase())))
                            .map(u => (
                                <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => { setViewingUser(u); setPickerOpen(false); setSelectedBatchIds(new Set()); }}
                                    className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-primary-50 ${viewingUser?.id === u.id ? "bg-primary-50 font-semibold text-primary-700" : "text-gray-700"}`}
                                >
                                    <span className="h-2 w-2 rounded-full bg-violet-400" />
                                    <span className="truncate">{u.label}</span>
                                </button>
                            ))}
                    </>
                )}
                {userList.filter(u => u.type === "STUDENT" && (!userSearch.trim() || u.label.toLowerCase().includes(userSearch.toLowerCase()))).length > 0 && (
                    <>
                        <div className="px-4 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                            {isCoachCalendar ? t("calendar.myRosterStudents") : t("calendar.students")}
                        </div>
                        {userList
                            .filter(u => u.type === "STUDENT" && (!userSearch.trim() || u.label.toLowerCase().includes(userSearch.toLowerCase())))
                            .map(u => (
                                <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => { setViewingUser(u); setPickerOpen(false); setSelectedBatchIds(new Set()); }}
                                    className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-primary-50 ${viewingUser?.id === u.id ? "bg-primary-50 font-semibold text-primary-700" : "text-gray-700"}`}
                                >
                                    <span className="h-2 w-2 rounded-full bg-sky-400" />
                                    <span className="truncate">{u.label}</span>
                                </button>
                            ))}
                    </>
                )}
                {userListLoadError ? (
                    <p className="px-4 py-3 text-center text-sm text-rose-700" role="status">{t('common.optionsLoadFailed')}</p>
                ) : isCoachCalendar && userList.filter(u => u.type === "STUDENT").length === 0 ? (
                    <p className="px-4 py-3 text-center text-sm text-gray-400">{t("calendar.noRosterStudents")}</p>
                ) : null}
                {!userListLoadError && !isCoachCalendar && userList.filter(u => !userSearch.trim() || u.label.toLowerCase().includes(userSearch.toLowerCase())).length === 0 && (
                    <p className="py-4 text-center text-sm text-gray-400">{t("calendar.noResults")}</p>
                )}
            </div>
        </div>,
        document.body
    );

    const statusBadges = viewMode === "month" ? (
        <div className="flex gap-2 text-sm flex-wrap">
            <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-medium">{t("calendar.pending", { count: counts["PENDING"] || 0 })}</span>
            <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">{t("calendar.completed", { count: counts["COMPLETED"] || 0 })}</span>
        </div>
    ) : null;

    const calendarSubtitle = role === "PARENT"
        ? t("calendar.subtitleParent")
        : role === "COACH"
            ? t("calendar.subtitleCoach")
        : readOnly
            ? t("calendar.subtitleStudent")
            : t("calendar.subtitleStaff");

    return (
        <ListPageLayout>
            <ParentPortalHeader />
            <ListPageHeader
                eyebrow={t("calendar.eyebrow")}
                title={t("calendar.title")}
                subtitle={calendarSubtitle}
                actions={statusBadges}
            />

            <div ref={containerRef} className="space-y-6">
                <ListSectionCard className="space-y-4 !mb-0 overflow-visible">
                    <div className="flex items-center gap-3 flex-wrap overflow-visible">
                        <button type="button" onClick={prev} aria-label={t("calendar.prev")} className="p-1.5 rounded-lg text-gray-500 hover:bg-purple-50 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        </button>

                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowPicker(p => !p)}
                                className="text-lg font-semibold text-gray-900 hover:text-purple-600 transition-colors min-w-[12rem] text-center"
                                aria-haspopup="dialog"
                                aria-expanded={showPicker}
                            >
                                {headerLabel}
                            </button>
                            {showPicker && (
                                <MonthYearPicker
                                    year={year}
                                    month={month}
                                    onSelect={(y, m) => { setYear(y); setMonth(m); setWeekAnchor(startOfWeek(new Date(y, m, 1))); }}
                                    onClose={() => setShowPicker(false)}
                                />
                            )}
                        </div>

                        <button type="button" onClick={next} aria-label={t("calendar.next")} className="p-1.5 rounded-lg text-gray-500 hover:bg-purple-50 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>

                        <button type="button" onClick={goToday} className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-600 hover:bg-purple-50 hover:border-purple-200 transition-colors">
                            {t("calendar.today")}
                        </button>

                        <div className="calendar-view-toggle">
                            <button
                                type="button"
                                onClick={() => setViewMode("month")}
                                className={viewMode === "month" ? "is-active" : ""}
                            >
                                {t("calendar.month")}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setViewMode("week"); setWeekAnchor(startOfWeek(new Date(year, month, today.getDate()))); }}
                                className={viewMode === "week" ? "is-active" : ""}
                            >
                                {t("calendar.week")}
                            </button>
                        </div>

                        {canUseViewingPicker && (
                            <div className="calendar-viewing-picker">
                                <span className="calendar-viewing-picker-label">{t("calendar.viewing")}</span>
                                <button
                                    ref={pickerButtonRef}
                                    type="button"
                                    aria-haspopup="listbox"
                                    aria-expanded={pickerOpen}
                                    onClick={() => { setPickerOpen((p) => !p); setUserSearch(""); }}
                                    className={`calendar-viewing-picker-trigger ${pickerOpen ? "is-open" : ""}`}
                                >
                                    <svg className="h-4 w-4 shrink-0 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    <span className="truncate">
                                        {viewingUser ? viewingUser.label : defaultViewLabel}
                                    </span>
                                    {viewingUser && (
                                        <span className="shrink-0 text-[10px] font-normal text-gray-400">
                                            {viewingUser.type === "STUDENT" ? t("calendar.student") : t("calendar.coach")}
                                        </span>
                                    )}
                                    <svg className={`ml-0.5 h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${pickerOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </div>
                        )}

                        <div className="ml-auto"><Legend readOnly={readOnly} useBatchColor={useBatchColor} /></div>
                    </div>
                    {viewingPickerMenu}

                    {useBatchColor && (
                        <BatchFilterBar
                            batches={batchList}
                            selectedBatchIds={selectedBatchIds}
                            onSelectBatch={selectBatch}
                            onShowAll={showAllBatches}
                        />
                    )}

                    <p className="text-[10px] text-gray-400 hidden sm:block">
                        {t("calendar.shortcuts")}
                        {!readOnly && t("calendar.shortcutsStaff")}
                    </p>
                </ListSectionCard>

                {error && (
                    <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                        {error}
                    </div>
                )}
                {holidayLoadError && !error && (
                    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                        {t("calendar.holidaysLoadFailed", {
                            defaultValue: "Holidays could not be loaded. The calendar may look like a normal work week.",
                        })}
                    </div>
                )}

                <ListTableCard className="relative">
                    {viewMode === "month" ? (
                        <>
                        {monthHasNoSessions && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                                <div className="bg-white/90 backdrop-blur-sm rounded-xl px-6 py-4 text-center shadow border border-gray-100">
                                    <p className="text-gray-600 font-medium">{t('calendar.noSessionsMonth', { month: monthName(t, month), year })}</p>
                                    <p className="text-sm text-gray-400 mt-1">{t('calendar.noSessionsHint')}</p>
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                            {Array.from({ length: 7 }, (_, i) => {
                                const d = dayShortFromIndex(t, i);
                                return (
                                <div key={d} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide border-r last:border-r-0 border-gray-200">
                                    {d}
                                </div>
                                );
                            })}
                        </div>
                        <div className="grid grid-cols-7">
                            {gridDays.map((date, idx) => {
                                const key = toIso(date);
                                return (
                                    <DayCell
                                        key={idx}
                                        date={date}
                                        isCurrentMonth={date.getMonth() === month}
                                        isToday={key === todayKey}
                                        sessions={byDate[key] || []}
                                        baseUrl={baseUrl}
                                        navigate={navigate}
                                        readOnly={readOnly}
                                        useBatchColor={useBatchColor}
                                        onDayClick={handleDayClick}
                                        onShowAll={(d, list) => setDayModal({ date: d, sessions: list })}
                                        holidayLabel={holidayByDate[key]}
                                    />
                                );
                            })}
                        </div>
                        </>
                    ) : (
                    <WeekView
                        weekStart={weekStart}
                        sessions={visibleSessions}
                        baseUrl={baseUrl}
                        navigate={navigate}
                        readOnly={readOnly}
                        useBatchColor={useBatchColor}
                        onDayClick={handleDayClick}
                        onShowAll={(d, list) => setDayModal({ date: d, sessions: list })}
                    />
                )}
                </ListTableCard>

            {dayModal && (
                <DayDetailModal
                    date={dayModal.date}
                    sessions={dayModal.sessions}
                    onClose={() => setDayModal(null)}
                    childrenProps={{
                        renderSessionRow: (s) => (
                            <div className="flex flex-col gap-2">
                                <SessionChip session={s} {...chipRowProps} />
                                {readOnly ? (
                                    <div className="flex justify-center">
                                        <SessionJoinAction
                                            sessionId={s.sessionId}
                                            role={role}
                                            classroomEligible={s.classroomEligible}
                                            canJoin={s.canJoin}
                                            meetingStatus={s.meetingStatus}
                                            status={s.status}
                                            endTime={s.endTime}
                                            studentId={childStudentId}
                                            compact
                                        />
                                    </div>
                                ) : (viewingUser && viewingUser.type === "COACH") ? (
                                    <p className="text-center text-xs text-slate-500">
                                        {t("calendar.viewingOtherCoachNoHost", {
                                            defaultValue: "Viewing another coach — open your calendar to host.",
                                        })}
                                    </p>
                                ) : (
                                    <div className="flex justify-center">
                                        <SessionHostAction
                                            sessionId={s.sessionId}
                                            role={role}
                                            classroomEligible={s.classroomEligible}
                                            hasMeeting={s.hasMeeting}
                                            meetingStatus={s.meetingStatus}
                                            status={s.status}
                                            endTime={s.endTime}
                                            compact
                                            onEnded={() => {
                                                const patch = (row) => (
                                                    row.sessionId === s.sessionId
                                                        ? { ...row, meetingStatus: 'ENDED', hasMeeting: true, canJoin: false }
                                                        : row
                                                );
                                                setSessions((rows) => rows.map(patch));
                                                setDayModal((prev) => {
                                                    if (!prev) return prev;
                                                    return { ...prev, sessions: (prev.sessions || []).map(patch) };
                                                });
                                            }}
                                            onStarted={() => {
                                                const patch = (row) => (
                                                    row.sessionId === s.sessionId
                                                        ? { ...row, meetingStatus: 'STARTED', hasMeeting: true }
                                                        : row
                                                );
                                                setSessions((rows) => rows.map(patch));
                                                setDayModal((prev) => {
                                                    if (!prev) return prev;
                                                    return { ...prev, sessions: (prev.sessions || []).map(patch) };
                                                });
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        ),
                    }}
                />
            )}
            </div>
        </ListPageLayout>
    );
}
